import { useCallback, useEffect, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input } from '@cloudflare/kumo/components/input';
import { Select } from '@cloudflare/kumo/components/select';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { api } from '@/lib/api';

// 当日の欠席・遅刻連絡 (出展者詳細)。事前に連絡があった分を日ごとに付ける。
// - 欠席: 受付端末の未受付の一覧と「本日の受付」の数から外す
// - 遅刻連絡: 未受付の一覧に残したまま「遅刻連絡 11:00頃」と出す
// 出展しない日の欄は出さない。

type StatusValue = 'none' | 'absent' | 'late';

interface Row {
  eventDay: number;
  status: 'absent' | 'late';
  expectedTime: string | null;
}

const OPTIONS: { value: StatusValue; label: string }[] = [
  { value: 'none', label: '連絡なし' },
  { value: 'absent', label: '欠席' },
  { value: 'late', label: '遅刻連絡あり' },
];

const DAYS: { day: 1 | 2; exhibitDay: string; label: string }[] = [
  { day: 1, exhibitDay: 'saturday', label: '1 日目 (土曜)' },
  { day: 2, exhibitDay: 'sunday', label: '2 日目 (日曜)' },
];

export default function DayStatusPanel({ exhibitorId, exhibitDays }: { exhibitorId: string; exhibitDays: string[] }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ data: Row[] }>(`/api/admin/checkins/day-statuses/${exhibitorId}`);
      setRows(res.data.data);
    } catch {
      setError('欠席・遅刻連絡の読み込みに失敗しました');
    }
  }, [exhibitorId]);

  useEffect(() => {
    load();
  }, [load]);

  const days = DAYS.filter((d) => exhibitDays.includes(d.exhibitDay));

  return (
    <Surface className="p-6">
      <h2 className="text-base font-semibold text-kumo-default">当日の欠席・遅刻連絡</h2>
      <p className="mt-1 text-xs text-kumo-muted">
        事前に連絡があったときに付けます。欠席の日は受付端末の未受付の一覧と受付数から外れ、遅刻連絡は未受付の一覧に表示されます。
      </p>
      {error && (
        <div className="mt-3">
          <Banner variant="error">{error}</Banner>
        </div>
      )}
      {rows && (
        <div className="mt-4 flex flex-col gap-4">
          {days.length === 0 && <span className="text-sm text-kumo-muted">出展日が設定されていません</span>}
          {days.map((d) => (
            <DayRow
              key={d.day}
              exhibitorId={exhibitorId}
              day={d.day}
              label={d.label}
              current={rows.find((r) => r.eventDay === d.day) ?? null}
              onSaved={load}
            />
          ))}
        </div>
      )}
    </Surface>
  );
}

function DayRow({
  exhibitorId,
  day,
  label,
  current,
  onSaved,
}: {
  exhibitorId: string;
  day: 1 | 2;
  label: string;
  current: Row | null;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<StatusValue>(current?.status ?? 'none');
  const [time, setTime] = useState(current?.expectedTime ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const dirty = status !== (current?.status ?? 'none') || (status === 'late' && time !== (current?.expectedTime ?? ''));
  const timeValid = time === '' || /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/api/admin/checkins/day-statuses/${exhibitorId}/${day}`, {
        status: status === 'none' ? null : status,
        expectedTime: status === 'late' && time ? time : null,
      });
      setMessage({ kind: 'ok', text: '保存しました' });
      onSaved();
    } catch {
      setMessage({ kind: 'error', text: '保存に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-32 pb-2 text-sm font-semibold text-kumo-default">{label}</div>
      <div className="w-44">
        <Select<string>
          value={status}
          onValueChange={(v) => v && setStatus(v as StatusValue)}
          renderValue={(v) => OPTIONS.find((o) => o.value === v)?.label ?? '-'}
        >
          {OPTIONS.map((o) => (
            <Select.Option key={o.value} value={o.value}>
              {o.label}
            </Select.Option>
          ))}
        </Select>
      </div>
      {status === 'late' && (
        <div className="w-40">
          <Field label="到着予定 (例: 11:00、空欄で不明)">
            <Input value={time} onChange={(e) => setTime(e.target.value.trim())} placeholder="11:00" />
          </Field>
        </div>
      )}
      <Button variant="primary" loading={saving} disabled={!dirty || !timeValid} onClick={save}>
        保存
      </Button>
      {!timeValid && <span className="pb-2 text-xs text-kumo-danger">時刻は 11:00 の形で入れてください</span>}
      {message && (
        <span className={`pb-2 text-xs ${message.kind === 'ok' ? 'text-kumo-success' : 'text-kumo-danger'}`}>
          {message.text}
        </span>
      )}
    </div>
  );
}
